// Form rules for roles and issue categories. They mirror the backend so the
// obvious mistakes are caught before a round trip; the server still answers
// 409/422 for the rest (key taken, unknown role, ...).

export const KEY_PATTERN = /^[a-z0-9_]{2,64}$/;

export const ROLE_KINDS = [
  'technician',
  'operations',
  'supervisor',
  'building_management',
];

export const SEVERITIES = ['low', 'medium', 'high', 'urgent'];
export const SLA_TIERS = ['T0', 'T1', 'T2', 'T3'];
export const VISIT_OFFERS = ['at_triage', 'on_pic_request', 'never'];

export const VISIT_MINUTES_MIN = 15;
export const VISIT_MINUTES_MAX = 480;

const LABEL_MAX = 120;

export function emptyRole() {
  return { key: '', label: '', kind: 'technician', active: true };
}

export function roleToForm(role) {
  return {
    key: role.key,
    label: role.label,
    kind: role.kind,
    active: !!role.active,
  };
}

// Returns { field: CODE } with the codes of CO_LIVING.FORM_ERRORS.
export function validateRole(form, { isEdit }) {
  const errors = {};
  if (!isEdit && !KEY_PATTERN.test(form.key)) errors.key = 'KEY';
  const label = form.label.trim();
  if (!label || label.length > LABEL_MAX) errors.label = 'LABEL';
  if (!ROLE_KINDS.includes(form.kind)) errors.kind = 'REQUIRED';
  return errors;
}

// `key` is immutable, so it only goes in the create body (and in the URL on
// update).
export function rolePayload(form, { isEdit }) {
  const body = {
    label: form.label.trim(),
    kind: form.kind,
    active: form.active,
  };
  return isEdit ? body : { key: form.key, ...body };
}

export function emptyCategory() {
  return {
    key: '',
    name: '',
    description: '',
    severity: 'medium',
    sla_tier: 'T2',
    emergency: false,
    visit_minutes: 60,
    visit_offer: 'on_pic_request',
    playbook: '',
    active: true,
  };
}

export function categoryToForm(category) {
  return {
    key: category.key,
    name: category.name,
    description: category.description || '',
    severity: category.severity,
    sla_tier: category.sla_tier,
    emergency: !!category.emergency,
    visit_minutes: category.visit_minutes,
    visit_offer: category.visit_offer,
    playbook: category.playbook || '',
    active: !!category.active,
  };
}

export function validateCategory(form, { isEdit }) {
  const errors = {};
  if (!isEdit && !KEY_PATTERN.test(form.key)) errors.key = 'KEY';
  const name = form.name.trim();
  if (!name || name.length > LABEL_MAX) errors.name = 'LABEL';
  const minutes = Number(form.visit_minutes);
  if (
    form.visit_minutes === '' ||
    !Number.isInteger(minutes) ||
    minutes < VISIT_MINUTES_MIN ||
    minutes > VISIT_MINUTES_MAX
  ) {
    errors.visit_minutes = 'VISIT_MINUTES';
  }
  return errors;
}

export function categoryPayload(form, { isEdit }) {
  const body = {
    name: form.name.trim(),
    // Empty strings, not null: the request schema is not documented as
    // nullable, and an empty string is valid either way.
    description: form.description.trim(),
    severity: form.severity,
    sla_tier: form.sla_tier,
    emergency: form.emergency,
    visit_minutes: Number(form.visit_minutes),
    visit_offer: form.visit_offer,
    playbook: form.playbook.trim(),
    active: form.active,
  };
  return isEdit ? body : { key: form.key, ...body };
}

// Emergency keywords are one account setting: a list of strings. The page
// edits them as comma-separated text.
export function parseKeywords(text) {
  const seen = new Set();
  return String(text || '')
    .split(',')
    .map(word => word.trim())
    .filter(word => {
      const key = word.toLowerCase();
      if (!word || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export const formatKeywords = list => (list || []).join(', ');

// "members, routing" -> the i18n keys of CO_LIVING.IN_USE for a 409 in_use.
export const usedByKeys = detail =>
  (Array.isArray(detail?.used_by) ? detail.used_by : []).map(item =>
    String(item).toUpperCase()
  );
